import useCurrentState from '@lib/hooks/useCurrentState'
import clsx from 'clsx'
import localFont from 'next/font/local'
import React, { MouseEvent } from 'react'
import CssTransition from '../css-transition'

interface Props {
  onClick?: (event: MouseEvent<HTMLElement>) => void
  visible?: boolean
  width?: string
  onContentClick?: (event: MouseEvent<HTMLElement>) => void
  backdropClassName?: string
  positionClassName?: string
  layerClassName?: string
}

const defaultProps = {
  onClick: () => {},
  visible: false,
  onContentClick: () => {},
  backdropClassName: '',
  positionClassName: '',
  layerClassName: '',
}
const helvetica = localFont({
  src: '../../../assets/fonts/helvetical.woff2',
  variable: '--helvetica',
  fallback: ['Helvetica Neue LT Std Roman', 'Helvetica', 'Arial', 'sans-serif'],
})
const helveticaMd = localFont({
  src: '../../../assets/fonts/helvetical.woff2',
  variable: '--helvetica-md',
  fallback: ['Helvetica Neue LT Std Roman', 'Helvetica', 'Arial', 'sans-serif'],
})
const kingscaslon = localFont({
  src: [
    {
      path: '../../../assets/fonts/kingscaslon-regular.woff2',
      style: 'normal',
    },
    {
      path: '../../../assets/fonts/kingscaslon-italic.woff2',
      style: 'italic',
    },
  ],
  variable: '--kingscaslon',
  fallback: ['Kings Caslon Display Typo', 'Times New Roman', 'Times', 'serif'],
})
const lato = localFont({
  src: '../../../assets/fonts/lato-regular.ttf',
  variable: '--lato',
})

type NativeAttrs = Omit<React.HTMLAttributes<any>, keyof Props>
export type BackdropProps = Props & NativeAttrs

const Backdrop: React.FC<React.PropsWithChildren<BackdropProps>> = React.memo(
  ({
    children,
    onClick,
    visible,
    width,
    onContentClick,
    backdropClassName,
    positionClassName,
    layerClassName,
    ...props
  }: React.PropsWithChildren<BackdropProps> & typeof defaultProps) => {
    const [, setIsContentMouseDown, IsContentMouseDownRef] =
      useCurrentState(false)
    const clickHandler = (event: MouseEvent<HTMLElement>) => {
      if (IsContentMouseDownRef.current) return
      onClick && onClick(event)
    }
    const mouseUpHandler = () => {
      if (!IsContentMouseDownRef.current) return
      const timer = setTimeout(() => {
        setIsContentMouseDown(false)
        clearTimeout(timer)
      }, 0)
    }

    return (
      <CssTransition
        name="backdrop-wrapper"
        visible={visible}
        clearTime={300}
        className={clsx(
          helvetica.variable,
          helveticaMd.variable,
          kingscaslon.variable,
          lato.variable
        )}
      >
        <div
          className={clsx('backdrop', backdropClassName)}
          onClick={clickHandler}
          onMouseUp={mouseUpHandler}
          {...props}
        >
          <div className={clsx('layer', layerClassName)} />
          <div
            onClick={onContentClick}
            className={clsx('position', positionClassName)}
            onMouseDown={() => setIsContentMouseDown(true)}
          >
            {children}
          </div>
          <style jsx>{`
            .backdrop {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              overflow: auto;
              z-index: 100000;
              -webkit-overflow-scrolling: touch;
              box-sizing: border-box;
              text-align: center;
            }
            .position {
              position: relative;
              z-index: 1001;
              outline: none;
              max-width: 90%;
              width: ${width};
              margin: 20px auto;
              vertical-align: middle;
              display: inline-block;
            }
            .backdrop:before {
              display: inline-block;
              width: 0;
              height: 100%;
              vertical-align: middle;
              content: '';
            }
            .layer {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              width: 100%;
              height: 100%;
              opacity: 0.75;
              background-color: black;
              transition: opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1);
              pointer-events: none;
              z-index: 1000;
            }
            .backdrop-wrapper-enter .layer {
              opacity: 0;
            }
            .backdrop-wrapper-enter-active .layer {
              opacity: 0.75;
            }
            .backdrop-wrapper-leave .layer {
              opacity: 0.75;
            }
            .backdrop-wrapper-leave-active .layer {
              opacity: 0;
            }
          `}</style>
        </div>
      </CssTransition>
    )
  }
)

Backdrop.defaultProps = defaultProps
Backdrop.displayName = 'GeistBackdrop'
export default Backdrop
